import { AppSpecData } from '@wix/thunderbolt-symbols'
import { UrlWithParsedQuery, parse } from 'url'
import _ from 'lodash'

const DATA_BINDING_APP_DEF_ID = 'dataBinding'

export function createDatabindingAppData(wixCodeAppData: AppSpecData, dbsmViewerApp: string): any {
	return {
		appDefinitionId: DATA_BINDING_APP_DEF_ID,
		appDefinitionName: 'Data Binding',
		applicationId: 'fakeApplicationId',
		appFields: {
			platform: {
				viewerScriptUrl: `${dbsmViewerApp}/app${
					process.env.PACKAGE_NAME === 'thunderbolt-ds' ? '.verbose' : ''
				}.js`,
			},
		},
		type: 'application',
		instanceId: wixCodeAppData.instanceId,
		widgets: {},
		isWixTPA: true,
	}
}

function isAllowedHost(hostname: UrlWithParsedQuery['hostname'], url: string) {
	return (
		['localhost', 'static.parastorage.com', 'sled.wix.dev'].includes(hostname as string) &&
		!(hostname === 'static.parastorage.com' && url.includes('/unpkg/'))
	)
}

function isInternalService(
	servicePath: string,
	{ hostname, pathname }: Pick<UrlWithParsedQuery, 'hostname' | 'pathname'>
) {
	return hostname === 'bo.wix.com' && pathname && _.startsWith(pathname, servicePath)
}

function isViewerHostnameScriptSupported(overrideViewerScriptUrl: string, siteProtocol: string) {
	const { protocol = '', hostname = '', pathname = '' } = _.isString(overrideViewerScriptUrl)
		? parse(overrideViewerScriptUrl)
		: {}
	if (
		!protocol ||
		!protocol.startsWith('http') ||
		(siteProtocol === 'https:' && protocol === 'http:' && hostname !== 'localhost')
	) {
		return false
	}
	return (
		isAllowedHost(hostname, overrideViewerScriptUrl) ||
		isInternalService('/suricate/tunnel', { hostname, pathname })
	)
}

export function extractControllersUrlOverrideMap(url: UrlWithParsedQuery): Record<string, string> | null {
	const {
		query: { controllersUrlOverride },
	} = url
	return Boolean(controllersUrlOverride) && _.isString(controllersUrlOverride)
		? _(controllersUrlOverride.split(',')).invokeMap('split', '=').fromPairs().value()
		: null
}

export function extractOverridePlatformBaseUrls(url: UrlWithParsedQuery): Record<string, Record<string, string>> {
	const {
		query: { overridePlatformBaseUrls },
	} = url
	return Boolean(overridePlatformBaseUrls) && _.isString(overridePlatformBaseUrls)
		? _(overridePlatformBaseUrls.split(','))
				.invokeMap('split', '=')
				.fromPairs()
				.mapValues((rawBaseUrls) => JSON.parse(decodeURIComponent(rawBaseUrls)))
				.value()
		: {}
}

function extractSupportedScriptUrl(url: UrlWithParsedQuery, queryName: string): Record<string, string> | null {
	const extractedUrl = url?.query?.[queryName]

	return Boolean(extractedUrl) && _.isString(extractedUrl)
		? _(extractedUrl.split(','))
				.invokeMap('split', '=')
				.filter(([, overrideUrl]) => isViewerHostnameScriptSupported(overrideUrl, url.protocol || ''))
				.fromPairs()
				.value()
		: null
}

export function extractViewerScriptUrlOverrideMap(url: UrlWithParsedQuery): Record<string, string> | null {
	return extractSupportedScriptUrl(url, 'viewerPlatformOverrides')
}

export function extractWidgetsUrlOverrideMap(url: UrlWithParsedQuery): Record<string, string> | null {
	return extractSupportedScriptUrl(url, 'widgetsUrlOverride')
}
